import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Button,
  CheckBox,
  Column,
  InputTyping,
  Label,
  Modal,
  Row,
  Select,
  Table,
  Textarea,
} from '@drivesally/sally-ui';

import {grey} from '@drivesally/sally-ui/lib/libs/colors';

import LoadMoreRow from '../../../components/components/atoms/LoadMoreRow/LoadMoreRow';
import getTeamLabel from '../../lib/getTeamLabel.lib.crm';
import CrmTagPill from '../CrmTagPill/CrmTagPill';

const STEP_DRIVER = 'driver';
const STEP_BLOCKED = 'blocked';
const STEP_DETAILS = 'details';

const Search = styled.div`
  padding: 16px;
`;

const CreateForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DriverSummary = styled.div`
  color: #555;
  font-size: 14px;
  line-height: 1.4;
`;

const WarningBox = styled.div`
  background: #fff4e6;
  border: 1px solid #f5c78a;
  border-radius: 4px;
  color: #5c3d0e;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const WarningTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const WarningText = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TicketLink = styled.button`
  align-items: center;
  background: none;
  border: 1px solid #f5c78a;
  border-radius: 4px;
  color: #8a5a12;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  justify-content: space-between;
  padding: 10px 12px;
  text-align: left;

  &:hover {
    background: #ffe8c7;
  }
`;

const TagsList = styled.div`
  max-height: 180px;
  overflow-y: auto;
`;

const TagRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${grey};
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 6px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
  padding: 16px 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const driverColumns = [
  {key: 'name', label: 'Name', span: 2},
  {key: 'phone', label: 'Phone'},
  {key: 'branch', label: 'Branch'},
  {key: 'state', label: 'State'},
  {key: 'svid', label: 'Vehicle'},
];

const driverDisplayName = (driver) => {
  if (!driver) return '-';
  const name = [driver.first_name, driver.last_name].filter(Boolean).join(' ');
  return name || driver.sally_name || `Driver #${driver.id}`;
};

const CrmCreateTicketModal = ({
  assignedTeamId,
  checkingOpenTickets,
  creating,
  driverLocked,
  drivers,
  driversLoading,
  driversMore,
  onChangeDriver,
  onClose,
  onCreate,
  onDriverMore,
  onDriverSearch,
  onOpenExistingTicket,
  onSelectDriver,
  onSummaryChange,
  onTagToggle,
  onTeamChange,
  openTickets,
  search,
  selectedDriver,
  selectedTagIds,
  step,
  summary,
  tags,
  teams,
  visible,
}) => (
  <Modal
    title="New Support Ticket"
    icon="plus"
    size="large"
    visible={visible}
    onClose={onClose}
  >
    {(Content) => {
      if (step === STEP_DRIVER) {
        return (
          <Content padding="none">
            <Search>
              <InputTyping
                value={search}
                onChange={onDriverSearch}
                preIcon="search1"
                placeholder="Search for a driver"
                disabled={checkingOpenTickets}
                treshold={400}
              />
            </Search>
            <Table
              columns={driverColumns}
              loading={driversLoading || checkingOpenTickets}
              noBorder
              noRadius
              smallNoResultsLabel
            >
              {(TableRow) =>
                [
                  ...drivers.map((driver) => (
                    <TableRow
                      key={driver.id}
                      onClick={
                        checkingOpenTickets
                          ? undefined
                          : onSelectDriver(driver)
                      }
                    >
                      {(TableColumn) => [
                        <TableColumn key="name" span={2}>
                          {driverDisplayName(driver)}
                        </TableColumn>,
                        <TableColumn key="phone">
                          {driver.phone_number || '-'}
                        </TableColumn>,
                        <TableColumn key="branch">
                          {driver.sally_branch?.name || '-'}
                        </TableColumn>,
                        <TableColumn key="state">
                          {driver.state || '-'}
                        </TableColumn>,
                        <TableColumn key="svid">
                          {driver.activeRentalSvid || '-'}
                        </TableColumn>,
                      ]}
                    </TableRow>
                  )),
                  !driversLoading &&
                    !checkingOpenTickets &&
                    driversMore && (
                      <TableRow key="more" onClick={onDriverMore}>
                        {(TableColumn) => (
                          <TableColumn span={6}>
                            <LoadMoreRow />
                          </TableColumn>
                        )}
                      </TableRow>
                    ),
                ].filter(Boolean)
              }
            </Table>
          </Content>
        );
      }

      if (step === STEP_BLOCKED) {
        return (
          <Content>
            <WarningBox>
              <WarningTitle>
                {driverDisplayName(selectedDriver)} already has an open ticket
              </WarningTitle>
              <WarningText>
                Only one support ticket can be open at a time for a driver.
                Open the existing ticket below, or choose a different driver.
              </WarningText>
              <TicketList>
                {openTickets.map((ticket) => (
                  <TicketLink
                    key={ticket.id}
                    type="button"
                    onClick={onOpenExistingTicket(ticket)}
                  >
                    <span>Ticket #{ticket.id}</span>
                    <span>Open</span>
                  </TicketLink>
                ))}
              </TicketList>
              <Actions>
                {!driverLocked && (
                  <Button onClick={onChangeDriver}>Change driver</Button>
                )}
              </Actions>
            </WarningBox>
          </Content>
        );
      }

      return (
        <Content>
          <CreateForm>
            <DriverSummary>
              Creating ticket for{' '}
              <strong>{driverDisplayName(selectedDriver)}</strong>
              {selectedDriver?.sally_branch?.name
                ? ` · ${selectedDriver.sally_branch.name}`
                : ''}
              {selectedDriver?.state ? ` · ${selectedDriver.state}` : ''}
            </DriverSummary>
            <Label label="Assigned Team">
              <Select
                value={assignedTeamId}
                options={[
                  {value: '', label: 'Select a team'},
                  ...teams.map((team) => ({
                    value: `${team.id}`,
                    label: getTeamLabel(team),
                  })),
                ]}
                onChange={onTeamChange}
                disabled={creating}
                notFilterable
              />
            </Label>
            <Textarea
              value={summary}
              onChange={onSummaryChange}
              disabled={creating}
              placeholder="Optional summary for this ticket"
            >
              Summary
            </Textarea>
            <Label label="Tags">
              <TagsList>
                {!tags.length ? (
                  <EmptyState>No tags available.</EmptyState>
                ) : (
                  tags.map((tag) => (
                    <TagRow
                      key={tag.id}
                      onClick={creating ? undefined : onTagToggle(tag.id)}
                    >
                      <CheckBox
                        value={selectedTagIds.includes(tag.id)}
                        onChange={() => {}}
                        disabled={creating}
                        size="small"
                      />
                      <CrmTagPill
                        color={tag.color}
                        label={tag.tag}
                        small
                      />
                    </TagRow>
                  ))
                )}
              </TagsList>
            </Label>
            <Row>
              <Column>
                <Actions>
                  {!driverLocked && (
                    <Button disabled={creating} onClick={onChangeDriver}>
                      Change driver
                    </Button>
                  )}
                  <Button
                    theme="red"
                    loading={creating}
                    disabled={!assignedTeamId}
                    onClick={onCreate}
                  >
                    Create Ticket
                  </Button>
                </Actions>
              </Column>
            </Row>
          </CreateForm>
        </Content>
      );
    }}
  </Modal>
);

CrmCreateTicketModal.propTypes = {
  assignedTeamId: PropTypes.string,
  checkingOpenTickets: PropTypes.bool,
  creating: PropTypes.bool,
  driverLocked: PropTypes.bool,
  drivers: PropTypes.array,
  driversLoading: PropTypes.bool,
  driversMore: PropTypes.bool,
  onChangeDriver: PropTypes.func,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onDriverMore: PropTypes.func,
  onDriverSearch: PropTypes.func,
  onOpenExistingTicket: PropTypes.func,
  onSelectDriver: PropTypes.func,
  onSummaryChange: PropTypes.func,
  onTagToggle: PropTypes.func,
  onTeamChange: PropTypes.func,
  openTickets: PropTypes.array,
  search: PropTypes.string,
  selectedDriver: PropTypes.object,
  selectedTagIds: PropTypes.array,
  step: PropTypes.oneOf([STEP_DRIVER, STEP_BLOCKED, STEP_DETAILS]),
  summary: PropTypes.string,
  tags: PropTypes.array,
  teams: PropTypes.array,
  visible: PropTypes.bool,
};

export {STEP_DRIVER, STEP_BLOCKED, STEP_DETAILS};
export default CrmCreateTicketModal;
